import Phaser from 'phaser';
import { SCENES } from '../config/constants';
import { economyConfig } from '../config/economyConfig';
import { gameStore } from '../simulation/GameStore';
import { fontPx, px } from '../ui/scale';
import { t, toggleLang } from '../ui/i18n';
import { AccountPanel } from '../ui/AccountPanel';

/**
 * MainMenuScene (spec sections 24/45): title + New Game / Load Game / Settings.
 * Rendered in Phaser so it is fully self-contained.
 */
export class MainMenuScene extends Phaser.Scene {
  constructor() {
    super(SCENES.MAIN_MENU);
  }

  create(): void {
    const { width, height } = this.scale;
    const cx = width / 2;

    this.cameras.main.setBackgroundColor('#0b1220');

    // --- Title ---
    this.add
      .text(cx, height * 0.22, 'PARKING TYCOON', {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(64),
        color: '#ffd54f',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    this.add
      .text(cx, height * 0.22 + px(56), t('menu.subtitle'), {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(20),
        color: '#90a4ae',
      })
      .setOrigin(0.5);

    // --- Buttons ---
    const hasSave = gameStore.hasSave();
    const startY = height * 0.5;
    const gap = px(70);

    this.createButton(cx, startY, t('menu.new'), () => this.startNewGame());
    this.createButton(
      cx,
      startY + gap,
      t('menu.load'),
      () => this.loadGame(),
      hasSave
    );
    this.createButton(cx, startY + gap * 2, t('menu.settings'), () =>
      this.openSettings()
    );

    this.createButton(cx, startY + gap * 3, t('menu.online'), () =>
      this.openOnline()
    );

    // Language toggle (Thai / English) — restarts the menu to re-render text.
    this.createButton(cx, startY + gap * 4, t('menu.lang'), () => {
      toggleLang();
      this.scene.restart();
    });

    this.add
      .text(cx, height - px(30), t('menu.phase'), {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(14),
        color: '#546e7a',
      })
      .setOrigin(0.5);
  }

  private createButton(
    x: number,
    y: number,
    label: string,
    onClick: () => void,
    enabled = true
  ): void {
    const w = px(280);
    const h = px(54);

    const bg = this.add
      .rectangle(x, y, w, h, enabled ? 0x1e88e5 : 0x37474f, 1)
      .setStrokeStyle(px(2), enabled ? 0x64b5f6 : 0x455a64)
      .setOrigin(0.5);

    const text = this.add
      .text(x, y, label, {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(22),
        color: enabled ? '#ffffff' : '#78909c',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);

    if (!enabled) return;

    bg.setInteractive({ useHandCursor: true });
    bg.on('pointerover', () => bg.setFillStyle(0x42a5f5));
    bg.on('pointerout', () => bg.setFillStyle(0x1e88e5));
    bg.on('pointerdown', () => bg.setFillStyle(0x1565c0));
    bg.on('pointerup', () => {
      bg.setFillStyle(0x42a5f5);
      onClick();
    });
    // Keep label above the interactive rect.
    text.setDepth(1);
  }

  private startNewGame(): void {
    gameStore.newGame({ companyName: economyConfig.DEFAULT_COMPANY_NAME });
    this.scene.start(SCENES.CITY);
  }

  private loadGame(): void {
    if (gameStore.loadGame()) {
      this.scene.start(SCENES.CITY);
    }
  }

  private openOnline(): void {
    const overlay = document.getElementById('ui-overlay');
    if (!overlay) return;
    const panel = new AccountPanel(overlay, gameStore, () => {
      this.scene.start(SCENES.CITY);
    });
    panel.open();
  }

  private openSettings(): void {
    // Phase 1 placeholder: a lightweight toast. A full settings panel comes
    // with the UI phase.
    const { width, height } = this.scale;
    const msg = this.add
      .text(width / 2, height * 0.86, t('menu.settingsSoon'), {
        fontFamily: 'Segoe UI, Tahoma, sans-serif',
        fontSize: fontPx(16),
        color: '#ffd54f',
        backgroundColor: '#1a2333',
        padding: { x: px(12), y: px(6) },
      })
      .setOrigin(0.5)
      .setAlpha(0);

    this.tweens.add({
      targets: msg,
      alpha: 1,
      duration: 150,
      yoyo: true,
      hold: 900,
      onComplete: () => msg.destroy(),
    });
  }
}
